import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { ImpersonationProvider } from "@/context/ImpersonationContext";
import { FeatureRouteGate, FeaturesProvider } from "@/context/FeaturesContext";
import { OngoingTreatmentRolloutProvider } from "@/context/OngoingTreatmentRolloutContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import BuildStamp from "@/components/BuildStamp";
import EnvironmentBadge from "@/components/EnvironmentBadge";
import AppShell from "@/layout/AppShell";
import DashboardV2 from "@/pages/DashboardV2";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Doctor from "@/pages/Doctor";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import AuthConfirmed from "@/pages/AuthConfirmed";
import Signup from "@/pages/Signup";
import PlanBuilder from "@/pages/PlanBuilder";
import JoinClinic from "@/pages/JoinClinic";
import Settings from "@/pages/Settings";
import SettingsTeam from "@/pages/SettingsTeam";
import SettingsBranches from "@/pages/SettingsBranches";
import SettingsClinic from "@/pages/SettingsClinic";
import SettingsAppointments from "@/pages/SettingsAppointments";
import SettingsServices from "@/pages/SettingsServices";
import SettingsChairs from "@/pages/SettingsChairs";
import SettingsPermissions from "@/pages/SettingsPermissions";
import SettingsEmailTemplates from "@/pages/SettingsEmailTemplates";
import SettingsRecallCenter from "@/pages/SettingsRecallCenter";
import SettingsMethods from "@/pages/SettingsMethods";
import SettingsOrtho from "@/pages/SettingsOrtho";
import SettingsQRCodes from "@/pages/SettingsQRCodes";
import SettingsProfile from "@/pages/SettingsProfile";
import SettingsBilling from "@/pages/SettingsBilling";
import SettingsSetupGuide from "@/pages/SettingsSetupGuide";
import SettingsInsurance from "@/pages/SettingsInsurance";
import SettingsTutorials from "@/pages/SettingsTutorials";
import SettingsReferrals from "@/pages/SettingsReferrals";
import SettingsSelfRegistration from "@/pages/SettingsSelfRegistration";
import TutorialDemo from "@/pages/TutorialDemo";
import BillingAccessGate from "@/components/billing/BillingAccessGate";
import PublicMedicalHistory from "@/pages/PublicMedicalHistory";
import PublicPatientRegistration from "@/pages/PublicPatientRegistration";
import PublicPatientOnboarding from "@/pages/PublicPatientOnboarding";
import PublicAppointmentConfirmation from "@/pages/PublicAppointmentConfirmation";
import Calendar from "@/pages/Calendar";
import Checkout from "@/pages/Checkout";
import CheckoutFutureNeedTutorialDemo from "@/pages/CheckoutFutureNeedTutorialDemo";
import Pay from "@/pages/Pay";
import RecallCenter from "@/pages/RecallCenter";
import TxPlanCenter from "@/pages/TxPlanCenter";
import OrthoCenter from "@/pages/OrthoCenter";
import Reports from "@/pages/Reports";
import Expenses from "@/pages/Expenses";
import ARCenter from "@/pages/ARCenter";
import Upgrade from "@/pages/Upgrade";
import Support from "@/pages/Support";
import InsuranceClaims from "@/pages/InsuranceClaims";
import InventoryPreview from "@/pages/InventoryPreview";
import ClinicModuleGate from "@/components/auth/ClinicModuleGate";

export default function App() {
  return (
    <AuthProvider>
      <EnvironmentBadge />
      <BuildStamp />
      <BrowserRouter>
        <ImpersonationProvider>
          <Routes>
            <Route path="/login"          element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/confirmed" element={<AuthConfirmed />} />
            <Route path="/plan-builder"   element={<PlanBuilder />} />
            <Route path="/signup"         element={<Signup />} />
            <Route path="/signup/clinic"  element={<Signup />} />
            <Route path="/ref/:referralCode" element={<Signup />} />
            <Route path="/join"           element={<JoinClinic />} />
            <Route path="/register/:token" element={<PublicPatientOnboarding />} />
            <Route path="/book/:token"     element={<PublicPatientOnboarding />} />
            <Route path="/medical-history/:token" element={<PublicMedicalHistory />} />
            <Route path="/r/:token"               element={<PublicPatientRegistration />} />
            <Route path="/pay/:token"             element={<Pay />} />
            <Route path="/appointment/confirm"    element={<PublicAppointmentConfirmation />} />
            <Route path="/confirm/:token"         element={<PublicAppointmentConfirmation />} />

            <Route
              element={(
                <ProtectedRoute>
                  <FeaturesProvider>
                    <OngoingTreatmentRolloutProvider>
                      <AppShell />
                    </OngoingTreatmentRolloutProvider>
                  </FeaturesProvider>
                </ProtectedRoute>
              )}
            >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard"      element={<DashboardV2 />} />
            <Route path="/dashboard-v2"   element={<DashboardV2 />} />
            <Route path="/patients"       element={<Patients />} />
            <Route path="/patients/:id"   element={<PatientDetail />} />
            <Route path="/calendar"       element={<Calendar />} />
            <Route path="/doctor"         element={<ClinicModuleGate moduleKey="chairside"><BillingAccessGate><Doctor /></BillingAccessGate></ClinicModuleGate>} />
            <Route path="/recall-center"  element={<BillingAccessGate><RecallCenter /></BillingAccessGate>} />
            <Route path="/tx-plans"       element={<ClinicModuleGate moduleKey="treatment_plans"><FeatureRouteGate feature="tx_plan_center"><BillingAccessGate><TxPlanCenter /></BillingAccessGate></FeatureRouteGate></ClinicModuleGate>} />
            <Route path="/ortho-center"   element={<ClinicModuleGate moduleKey="ortho"><FeatureRouteGate feature="ortho_module"><BillingAccessGate><OrthoCenter /></BillingAccessGate></FeatureRouteGate></ClinicModuleGate>} />
            <Route path="/checkout"       element={<ClinicModuleGate moduleKey="checkout"><BillingAccessGate><Checkout /></BillingAccessGate></ClinicModuleGate>} />
            <Route path="/ar-center"      element={<ClinicModuleGate moduleKey="accounts_receivable"><BillingAccessGate><ARCenter /></BillingAccessGate></ClinicModuleGate>} />
            <Route path="/insurance-claims" element={<ClinicModuleGate moduleKey="hmo_claims"><BillingAccessGate><InsuranceClaims /></BillingAccessGate></ClinicModuleGate>} />
            <Route path="/inventory" element={<ClinicModuleGate moduleKey="inventory"><BillingAccessGate><InventoryPreview /></BillingAccessGate></ClinicModuleGate>} />
            <Route path="/inventory-preview" element={<Navigate to="/inventory" replace />} />
            <Route path="/claims"         element={<Navigate to="/insurance-claims" replace />} />
            <Route path="/checkout/tutorial/future-need" element={<CheckoutFutureNeedTutorialDemo />} />
            <Route path="/checkout/tutorial/tx-plan" element={<Navigate to="/checkout?appointment_id=tutorial-checkout-tx-plan&tutorial=checkout-tx-planning&demo=1" replace />} />
            <Route path="/reports"        element={<ClinicModuleGate moduleKey="reports"><FeatureRouteGate anyOf={["reports", "clinic_health_calculator"]}><Reports /></FeatureRouteGate></ClinicModuleGate>} />
            <Route path="/reports/:reportId" element={<ClinicModuleGate moduleKey="reports"><FeatureRouteGate anyOf={["reports", "clinic_health_calculator"]}><Reports /></FeatureRouteGate></ClinicModuleGate>} />
            <Route path="/expenses"       element={<ClinicModuleGate moduleKey="expenses"><FeatureRouteGate feature="expenses"><BillingAccessGate><Expenses /></BillingAccessGate></FeatureRouteGate></ClinicModuleGate>} />
            <Route path="/support"        element={<ClinicModuleGate moduleKey="support"><Support /></ClinicModuleGate>} />
            <Route path="/upgrade"        element={<Upgrade />} />
            <Route path="/tutorials/demo/:tutorialId" element={<TutorialDemo />} />
            <Route path="/settings"       element={<Settings />}>
              <Route path="branches"       element={<BillingAccessGate><SettingsBranches /></BillingAccessGate>} />
              <Route path="clinic"         element={<BillingAccessGate><SettingsClinic /></BillingAccessGate>} />
              <Route path="appointments"   element={<BillingAccessGate><SettingsAppointments /></BillingAccessGate>} />
              <Route path="chairs"         element={<BillingAccessGate><SettingsChairs /></BillingAccessGate>} />
              <Route path="services"       element={<BillingAccessGate><SettingsServices /></BillingAccessGate>} />
              <Route path="team"           element={<BillingAccessGate><SettingsTeam /></BillingAccessGate>} />
              <Route path="permissions"    element={<BillingAccessGate><SettingsPermissions /></BillingAccessGate>} />
              <Route path="email-templates" element={<BillingAccessGate><SettingsEmailTemplates /></BillingAccessGate>} />
              <Route path="reminders"      element={<Navigate to="/settings/email-templates" replace />} />
              <Route path="recall"         element={<BillingAccessGate><SettingsRecallCenter /></BillingAccessGate>} />
              <Route path="self-registration" element={<BillingAccessGate><SettingsSelfRegistration /></BillingAccessGate>} />
              <Route path="qr-codes"       element={<BillingAccessGate><SettingsQRCodes /></BillingAccessGate>} />
              <Route path="methods"        element={<BillingAccessGate><SettingsMethods /></BillingAccessGate>} />
              <Route path="insurance"      element={<BillingAccessGate><SettingsInsurance /></BillingAccessGate>} />
              <Route path="referrals"      element={<SettingsReferrals />} />
              <Route path="ortho"          element={<FeatureRouteGate feature="ortho_module"><BillingAccessGate><SettingsOrtho /></BillingAccessGate></FeatureRouteGate>} />
              <Route path="profile"        element={<SettingsProfile />} />
              <Route path="billing"        element={<SettingsBilling />} />
              <Route path="setup-guide"    element={<SettingsSetupGuide />} />
              <Route path="tutorials"      element={<SettingsTutorials />} />
            </Route>
            <Route path="*"               element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        </ImpersonationProvider>
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
